import { castArray, get, isNil, orderBy } from 'lodash';

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface MapEntriesToRecordsOptions {
  /**
   * Attribute name(s) to sort the generated records on. Nested paths are supported (lodash `get` syntax).
   * When several names are provided they act as a fallback chain: the first non-empty value found on a
   * record wins (e.g. `['name', 'label', 'id']` sorts by `name`, falling back to `label`, then `id`).
   * When omitted, the original entry order is preserved.
   */
  sortBy?: string | string[];
  /** Sort direction. Defaults to `'asc'`. Ignored when `sortBy` is not provided. */
  sortOrder?: 'asc' | 'desc';
}

export class ArrayUtils {
  static pluck = (key: string) => (array: any[]) => Array.from(new Set(array.map((obj) => obj[key])));

  /**
   * Combine duplicates elements
   *
   * @usageNotes
   *
   * ```
   * const array = [
   *    { key:"manage", value:"0"},
   *    { key:"manage", value:"1"},
   *    { key:"manage", value:"2"},
   *    { key:"fab", value:"0"},
   *    { key:"fab", value:"1"},
   *    { key:"limit", value:"10"}
   *  ];
   *
   * let result = combineDuplicates('key', array)
   * console.log(result);   // [{ key: 'manage', values: '0,1,2' }, { key: 'fab', values: '0,1' }, { key: 'limit', values: '10' } ]
   *
   * result = combineDuplicates('key', array, ['fab'])
   * console.log(result);   // [{ key: 'manage', values: '0,1,2' }, { key: 'fab', values: '1' }, { key: 'limit', values: '10' } ]
   * ```
   *
   * @param criteriaKey the criteria key.
   * @param array selected filters.
   * @param exclusions Optional parameter. It represent excluded properties
   *
   * Excluded properties will get the last selected value in the filter
   */
  static combineDuplicates = (criteriaKey: string, array: { value: string; key: string }[], exclusions?: string[]) =>
    ArrayUtils.pluck(criteriaKey)(array).map((key: string) => {
      const values = array.filter((e) => e[criteriaKey] === key).map((e) => e.value);
      return {
        key,
        values: exclusions && exclusions.includes(key) ? values.pop() : values.join()
      };
    });

  static isEqual = (a: any[] = [], b: any[] = []): boolean => {
    const sortedA = a.sort((v1: string, v2: string) => v1.toString().localeCompare(v2.toString()));
    const sortedB = b.sort((v1: string, v2: string) => v1.toString().localeCompare(v2.toString()));
    return sortedA.length === sortedB.length && sortedA.every((element: any, index: number): boolean => element === sortedB[index]);
  };

  static diffByKeys(list1: any[], list2: any[], key1: string, key2?: string): any[] {
    return list1.filter((v1) => !list2.find((v2) => get(v1, key1) === get(v2, key2 ?? key1)));
  }

  static padEnd = (array: any[], minLength: number, fillValue: any) => Object.assign(new Array(minLength).fill(fillValue), array);

  /**
   * Turns a record's `[key, value]` entries into an array of objects, injecting the record key as `id`,
   * with an optional sort applied to the result.
   *
   * @usageNotes
   *
   * ```
   * const entries = Object.entries({ b: { name: 'Beta' }, a: { name: 'Alpha' } });
   *
   * ArrayUtils.mapEntriesToRecordsWithId(entries);
   * // [{ name: 'Beta', id: 'b' }, { name: 'Alpha', id: 'a' }]
   *
   * ArrayUtils.mapEntriesToRecordsWithId(entries, { sortBy: 'name' });
   * // [{ name: 'Alpha', id: 'a' }, { name: 'Beta', id: 'b' }]
   *
   * ArrayUtils.mapEntriesToRecordsWithId(entries, { sortBy: ['name', 'label', 'id'], sortOrder: 'desc' });
   * // [{ name: 'Beta', id: 'b' }, { name: 'Alpha', id: 'a' }]
   * ```
   *
   * @param entries the record entries, as returned by `Object.entries`.
   * @param options optional sort configuration, see {@link MapEntriesToRecordsOptions}.
   */
  static mapEntriesToRecordsWithId<T>(entries: [string, T][], options?: MapEntriesToRecordsOptions): (T & { id: string })[] {
    const records = entries.map(([id, record]) => ({
      ...record,
      id
    }));

    const sortBy = isNil(options?.sortBy) ? [] : castArray(options.sortBy);
    if (!sortBy.length) {
      return records;
    }

    return orderBy(records, [(record) => ArrayUtils.resolveSortValue(record, sortBy)], [options?.sortOrder ?? 'asc']);
  }

  /**
   * Resolves the value a record must be sorted on, walking the `sortBy` fallback chain and returning the
   * first value that is neither nullish nor an empty string. Strings are lower-cased so the sort is
   * case-insensitive.
   */
  private static resolveSortValue(record: any, sortBy: string[]): string | number | undefined {
    const value = sortBy.map((key) => get(record, key)).find((item) => !isNil(item) && item !== '');
    return typeof value === 'string' ? value.toLowerCase() : value;
  }
}
